import { z } from 'zod';

/**
 * Available tool types for agent configuration
 * Based on the Python tool registry in packages/python-agents
 */
export const AVAILABLE_AGENT_TOOLS = {
  // Date and time tools
  GetDateTool: {
    name: 'Get Date Tool',
    description: 'Get current date and time information',
    category: 'utilities',
  },

  // Resource tools - Discovery & Navigation
  ResourceSearchTool: {
    name: 'Resource Search',
    description: 'Search through available resources and documents',
    category: 'resources',
  },
  ResourceListTool: {
    name: 'Resource List',
    description: 'List available resources with filtering',
    category: 'resources',
  },
  ResourceStructureTool: {
    name: 'Resource Structure',
    description: 'Analyze the structure of resources',
    category: 'resources',
  },
  RelatedResourcesTool: {
    name: 'Related Resources',
    description: 'Find resources related to a given resource',
    category: 'resources',
  },
  ResourceSampleTool: {
    name: 'Resource Sample',
    description: 'Get a sample of resource content',
    category: 'resources',
  },
  ResourceSectionTool: {
    name: 'Resource Section',
    description: 'Extract specific sections from resources',
    category: 'resources',
  },
  ResourceFindLinesTool: {
    name: 'Find Lines in Resource',
    description: 'Find specific lines within a resource',
    category: 'resources',
  },
  ResourceReadLinesTool: {
    name: 'Read Lines from Resource',
    description: 'Read specific lines from a resource',
    category: 'resources',
  },

  // Resource tools - Analysis & Processing
  ResourceSummarizationTool: {
    name: 'Resource Summarization',
    description: 'Generate summaries of resources',
    category: 'analysis',
  },
  ResourceQuestionAnsweringTool: {
    name: 'Resource Q&A',
    description: 'Answer questions based on resource content',
    category: 'analysis',
  },
  ResourceStructuredExtractionTool: {
    name: 'Structured Extraction',
    description: 'Extract structured data from resources',
    category: 'analysis',
  },
  ResourceEntitiesExtractionTool: {
    name: 'Entity Extraction',
    description: 'Extract entities from resources',
    category: 'analysis',
  },
  ResourcePrioritizationTool: {
    name: 'Resource Prioritization',
    description: 'Prioritize resources based on relevance',
    category: 'analysis',
  },

  // Search tools
  TavilySearchTool: {
    name: 'Web Search (Tavily)',
    description: 'Search the web using Tavily search engine',
    category: 'search',
  },
  GenericSearchTool: {
    name: 'Generic Search',
    description: 'General purpose search functionality',
    category: 'search',
  },
  PatentSearchTool: {
    name: 'Patent Search',
    description: 'Search patent databases',
    category: 'search',
  },
  PubmedSearchTool: {
    name: 'PubMed Search',
    description: 'Search medical literature in PubMed',
    category: 'search',
    configurable: true,
  },
  ClinicalTrialSearchTool: {
    name: 'Clinical Trials Search',
    description: 'Search clinical trials databases',
    category: 'search',
    configurable: true,
  },

  // Analysis and writing tools
  SummaryTool: {
    name: 'Summary Tool',
    description: 'Generate concise summaries of text',
    category: 'writing',
  },
  CriticalAnalysisTool: {
    name: 'Critical Analysis',
    description: 'Perform critical analysis of content',
    category: 'analysis',
  },
  DraftWritingTool: {
    name: 'Draft Writing',
    description: 'Generate written drafts and documents',
    category: 'writing',
  },
  WritingFeedbackTool: {
    name: 'Writing Feedback',
    description: 'Provide feedback on written content',
    category: 'writing',
  },
  ResourceGroundedEditorTool: {
    name: 'Grounded Editor',
    description: 'Edit content with resource references',
    category: 'writing',
  },

  // Specialized tools
  ClassifyEntitiesFromTextTool: {
    name: 'Entity Classification',
    description: 'Classify entities found in text',
    category: 'analysis',
  },
  RegexSearchTool: {
    name: 'Regex Search',
    description: 'Search using regular expressions',
    category: 'utilities',
  },
  ScrapeWebsiteTool: {
    name: 'Website Scraper',
    description: 'Extract content from websites',
    category: 'utilities',
  },
  ResourceCreationTool: {
    name: 'Resource Creation',
    description: 'Create new resources',
    category: 'resources',
  },
  AskAgentRunTool: {
    name: 'Ask Agent',
    description: 'Query another agent for information',
    category: 'utilities',
  },
} as const;

export type AvailableToolType = keyof typeof AVAILABLE_AGENT_TOOLS;

export const ToolCategorySchema = z.enum([
  'utilities',
  'resources',
  'analysis',
  'search',
  'writing',
]);

export type ToolCategory = z.infer<typeof ToolCategorySchema>;

// Helper to get tools by category
export function getToolsByCategory(category: ToolCategory): Array<{
  type: AvailableToolType;
  name: string;
  description: string;
  configurable?: boolean;
}> {
  return Object.entries(AVAILABLE_AGENT_TOOLS)
    .filter(([_, tool]) => tool.category === category)
    .map(([type, tool]) => ({
      type: type as AvailableToolType,
      ...tool,
    }));
}

// Helper to validate tool type
export function isValidToolType(type: string): type is AvailableToolType {
  return type in AVAILABLE_AGENT_TOOLS;
}

// Common tool presets for quick setup
export const TOOL_PRESETS = {
  minimal: ['GetDateTool'] as AvailableToolType[],
  search: [
    'GetDateTool',
    'ResourceSearchTool',
    'TavilySearchTool',
  ] as AvailableToolType[],
  research: [
    'GetDateTool',
    'ResourceSearchTool',
    'ResourceListTool',
    'TavilySearchTool',
    'PubmedSearchTool',
    'SummaryTool',
  ] as AvailableToolType[],
  analysis: [
    'GetDateTool',
    'ResourceSearchTool',
    'ResourceSummarizationTool',
    'ResourceQuestionAnsweringTool',
    'CriticalAnalysisTool',
    'SummaryTool',
  ] as AvailableToolType[],
  writing: [
    'GetDateTool',
    'ResourceSearchTool',
    'DraftWritingTool',
    'WritingFeedbackTool',
    'ResourceGroundedEditorTool',
    'SummaryTool',
  ] as AvailableToolType[],
} as const;

export type ToolPreset = keyof typeof TOOL_PRESETS;
